import { cn } from '@shadcn/lib/utils';
import { Input as ShadcnInput, IInputProps as IShadcnInputProps } from '@shadcn/ui/input';
import { cva } from 'class-variance-authority';
import { LucideIcon } from 'lucide-react';
import React, { ReactNode } from 'react';

interface InputBaseProps extends Omit<IShadcnInputProps, 'size' | 'prefix'> {
  readonly suffix?: ReactNode;
  readonly size?: keyof typeof Size;
  readonly variant?: 'default' | 'error';
}

interface InputPropsWithIcon extends InputBaseProps {
  readonly icon?: LucideIcon;
  prefix?: never;
}

interface InputPropsWithPrefix extends InputBaseProps {
  readonly prefix?: ReactNode;
  icon?: never;
}

type InputProps = InputPropsWithIcon | InputPropsWithPrefix;

const Size = {
  small: 'small',
  medium: 'medium',
} as const;

const DEFAULT_ICON_SIZE = 16;

const containerVariants = cva(
  'flex items-center text-teal-dark border rounded-md shadow-sm transition-colors aria-disabled:opacity-50 focus-within:ring-1 focus-within:ring-ring bg-neutral-0 px-[12px]',
  {
    variants: {
      size: {
        [Size.small]: 'text-sm h-[29px]',
        [Size.medium]: 'text-base h-[43px]',
      },
      variant: {
        default: 'border-neutral-400',
        error: 'border-error',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ suffix, prefix, size = Size.medium, variant = 'default', icon: Icon, disabled, ...props }, ref) => (
    <div aria-disabled={disabled} className={containerVariants({ size, variant })}>
      {Icon && (
        <div className="flex items-center mr-2">
          <Icon color="var(--color-neutral-700)" size={DEFAULT_ICON_SIZE} />
        </div>
      )}
      {prefix && (
        <div className="flex items-center mr-3">
          <div className="text-neutral-700">{prefix}</div>
        </div>
      )}
      <ShadcnInput
        className={cn(
          'placeholder-abyssyl-blue-light/30 h-full w-full bg-neutral-0',
          { 'py-[6px]': size === Size.small },
          { 'py-[12px]': size === Size.medium }
        )}
        disabled={disabled}
        ref={ref}
        {...props}
      />
      {suffix && <div className="ml-3 text-neutral-700">{suffix}</div>}
    </div>
  )
);

export { Input };
